import { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CalendarClock, ChevronLeft, IdCard } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchScheduledBroadcasts, type AdminBroadcast } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/** A15.12 — rejalashtirilgan e'lonlar (scheduled_at IS NOT NULL). */
export function ScheduledBroadcasts() {
  const t = useT()
  const navigate = useNavigate()

  const rowActions = useMemo<RowAction<AdminBroadcast>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/broadcasts/${r.id}`),
      },
    ],
    [t, navigate],
  )

  const columns = useMemo<AdminColumn<AdminBroadcast>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      { key: 'scheduled_at', header: t.broadcast.schedule, type: 'date', width: 160 },
      { key: 'title', header: t.broadcast.titleField, type: 'text', minWidth: 160 },
      { key: 'body', header: t.broadcast.bodyField, type: 'longtext', minWidth: 220 },
      {
        key: 'audience',
        header: t.broadcast.audience,
        type: 'enum',
        width: 120,
        options: (['all', 'active', 'staff'] as const).map((a) => ({ value: a, label: t.broadcast[a], status: 'review' })),
      },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'draft', label: t.broadcast.draft, status: 'pending' },
          { value: 'queued', label: t.broadcast.queued, status: 'review' },
          { value: 'sent', label: t.broadcast.sent, status: 'active' },
          { value: 'cancelled', label: t.broadcast.cancelled, status: 'deleted' },
        ],
      },
    ],
    [t],
  )

  const fetcher = useCallback(fetchScheduledBroadcasts, [])

  return (
    <>
      <PageHeader
        title={t.broadcast.scheduledTitle}
        screenId="A15.12"
        actions={
          <Link
            to="/broadcasts"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.broadcast.title}
          </Link>
        }
      />
      <DataTable<AdminBroadcast>
        screenId="A15.12"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <CalendarClock size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
